import { useMemo } from 'react';

import { IIndirectTaxFilingV2ServiceRequestAssociation } from '@commenda/api/indirect-tax/filings/types';
import { useToggle } from '@commenda/kakashi';
import { TaskParty } from '@prisma/client';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IndirectTaxTaskAccordion } from '../../common/components';
import { IndirectTaxFilingsHelper } from '../helpers';
import {
  IIndirectTaxFilingTask,
  IndirectTaxFilingClientActionType,
} from '../types';
import { IndirectTaxFilingApproveModal } from './IndirectTaxFilingApproveModal';
import { IndirectTaxFilingDetailTaskTable } from './IndirectTaxFilingDetailTaskTable';
import { IndirectTaxFilingReportIssueModal } from './IndirectTaxFilingReportIssueModal';

interface IndirectTaxFilingDetailTaskBarProps {
  tasks: IIndirectTaxFilingTask[];
  filingId: string;
  corporationId: number;
  region: IndirectTaxRegion;
  serviceRequest?: IIndirectTaxFilingV2ServiceRequestAssociation | null;
  onTaskCompleted?: () => void;
  // Party viewing the filing. Agents can monitor the pending step but cannot
  // act on client-only actions (approve / report issue).
  viewerRole?: TaskParty;
}

const IndirectTaxFilingDetailTaskBar = ({
  tasks,
  filingId,
  corporationId,
  region,
  serviceRequest,
  onTaskCompleted,
  viewerRole = TaskParty.CLIENT,
}: IndirectTaxFilingDetailTaskBarProps) => {
  const pendingTasks = useMemo(
    () => IndirectTaxFilingsHelper.getPendingTasks(tasks),
    [tasks]
  );

  const approveModal = useToggle();
  const reportIssueModal = useToggle();

  const isAgentView = viewerRole === TaskParty.AGENT;
  const isContributionDriven =
    IndirectTaxRegionHelper.isContributionRegion(region);
  const isWorkflowDriven = Boolean(serviceRequest?.workflowExecutionId);
  const canReportIssue =
    !isAgentView &&
    Boolean(serviceRequest?.id) &&
    (isContributionDriven || isWorkflowDriven);

  if (pendingTasks.length === 0) return null;

  const handleTaskAction = (task: IIndirectTaxFilingTask) => {
    if (isAgentView) return;
    if (task.actionType === IndirectTaxFilingClientActionType.APPROVE) {
      approveModal.show();
    }
  };

  return (
    <>
      <IndirectTaxTaskAccordion
        pendingCount={pendingTasks.length}
        totalCount={pendingTasks.length}
        defaultOpen
      >
        <IndirectTaxFilingDetailTaskTable
          tasks={pendingTasks}
          onTaskAction={handleTaskAction}
          isContributionDriven={canReportIssue}
          onReportIssue={canReportIssue ? reportIssueModal.show : undefined}
          isActionDisabled={isAgentView}
        />
      </IndirectTaxTaskAccordion>

      <IndirectTaxFilingApproveModal
        isOpen={approveModal.visible}
        onClose={approveModal.off}
        filingId={filingId}
        corporationId={corporationId}
        onApproved={onTaskCompleted}
      />

      {canReportIssue && serviceRequest?.id ? (
        <IndirectTaxFilingReportIssueModal
          isOpen={reportIssueModal.visible}
          onClose={reportIssueModal.off}
          serviceRequestId={serviceRequest.id}
          onReported={() => onTaskCompleted?.()}
        />
      ) : null}
    </>
  );
};

export { IndirectTaxFilingDetailTaskBar };
